import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, API, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { GraduationCap, Plus, Paperclip } from '@phosphor-icons/react';

const TEACHERS = ['global_admin', 'school_admin', 'principal', 'registrar', 'teacher'];

const useUpload = () => {
  const [pending, setPending] = useState([]);
  const upload = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/lms/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPending((p) => [...p, r.data]);
      toast.success(`Attached ${r.data.name}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return { pending, setPending, upload };
};

const AttachmentLinks = ({ list }) => (list || []).length > 0 && (
  <div className="mt-1.5 space-y-2">
    {(list || []).filter((a) => a.content_type?.startsWith('video/')).map((a) => (
      <video key={a.file_id} controls preload="metadata" className="w-full max-w-md rounded-lg border border-slate-200" data-testid={`lesson-video-file-${a.file_id}`}
        src={`${API}/lms/files/${a.file_id}`} />
    ))}
    <div className="flex gap-2 flex-wrap">
      {(list || []).filter((a) => !a.content_type?.startsWith('video/')).map((a) => (
        <a key={a.file_id} href={`${API}/lms/files/${a.file_id}`} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline flex items-center gap-1"><Paperclip size={12} />{a.name}</a>
      ))}
    </div>
  </div>
);

const VideoEmbeds = ({ videos }) => (videos || []).length > 0 && (
  <div className="mt-2 grid gap-3 sm:grid-cols-2">
    {videos.map((v, i) => (
      <div key={v.embed_url} className="aspect-video rounded-lg overflow-hidden border border-slate-200" data-testid={`lesson-embed-${v.kind}-${i}`}>
        <iframe src={v.embed_url} title={`${v.kind}-${i}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen />
      </div>
    ))}
  </div>
);

export default function Lms() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isTeacher = TEACHERS.includes(user?.role);
  const { data: lessons } = useQuery({ queryKey: ['lms-lessons'], queryFn: () => api.get('/lms/lessons').then((r) => r.data) });
  const { data: assignments } = useQuery({ queryKey: ['lms-assignments'], queryFn: () => api.get('/lms/assignments').then((r) => r.data) });
  const { data: meta } = useQuery({ queryKey: ['meta-grades'], queryFn: () => api.get('/meta/grades').then((r) => r.data) });
  const { data: tenants } = useQuery({ queryKey: ['tenants'], queryFn: () => api.get('/tenants').then((r) => r.data), enabled: isTeacher });
  const school = (tenants || []).find((t) => t.type === 'school');
  const tid = user?.tenant_id || school?.id;
  const [lessonOpen, setLessonOpen] = useState(false);
  const [lform, setLform] = useState({ grade: '5', subject: '', title: '', content: '', video_text: '' });
  const [asgOpen, setAsgOpen] = useState(false);
  const [aform, setAform] = useState({ grade: '5', subject: '', title: '', instructions: '', due_at: '', max_marks: 10 });
  const [submitFor, setSubmitFor] = useState(null);
  const [subText, setSubText] = useState('');
  const [subsFor, setSubsFor] = useState(null);
  const lessonUp = useUpload();
  const submitUp = useUpload();
  const fileRef = useRef(null);
  const subFileRef = useRef(null);
  const { data: subs } = useQuery({ queryKey: ['lms-subs', subsFor?.id], queryFn: () => api.get(`/lms/assignments/${subsFor.id}/submissions`).then((r) => r.data), enabled: !!subsFor });

  const inval = () => { qc.invalidateQueries({ queryKey: ['lms-lessons'] }); qc.invalidateQueries({ queryKey: ['lms-assignments'] }); };
  const lessonMut = useMutation({
    mutationFn: () => api.post('/lms/lessons', { grade: lform.grade, subject: lform.subject, title: lform.title, content: lform.content, tenant_id: tid, attachments: lessonUp.pending, video_urls: lform.video_text.split('\n').map((x) => x.trim()).filter(Boolean) }),
    onSuccess: () => { toast.success('Lesson posted'); setLessonOpen(false); setLform({ grade: '5', subject: '', title: '', content: '', video_text: '' }); lessonUp.setPending([]); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const asgMut = useMutation({
    mutationFn: () => api.post('/lms/assignments', { ...aform, tenant_id: tid, max_marks: parseInt(aform.max_marks, 10) || 10 }),
    onSuccess: () => { toast.success('Assignment posted - students notified'); setAsgOpen(false); setAform({ grade: '5', subject: '', title: '', instructions: '', due_at: '', max_marks: 10 }); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delMut = useMutation({
    mutationFn: ({ kind, id }) => api.delete(`/lms/${kind}/${id}`),
    onSuccess: () => { toast.success('Deleted'); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const submitMut = useMutation({
    mutationFn: () => api.post(`/lms/assignments/${submitFor.id}/submit`, { text: subText, attachments: submitUp.pending }),
    onSuccess: (r) => { toast.success(r.data.late ? 'Submitted (late)' : 'Submitted'); setSubmitFor(null); setSubText(''); submitUp.setPending([]); inval(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const gradeMut = useMutation({
    mutationFn: ({ sid, marks, feedback }) => api.post(`/lms/submissions/${sid}/grade`, { marks, feedback }),
    onSuccess: () => { toast.success('Graded - student notified'); qc.invalidateQueries({ queryKey: ['lms-subs', subsFor?.id] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div data-testid="lms-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Lessons · assignments · online grading</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-2"><GraduationCap size={30} className="text-orange-600" weight="duotone" /> LMS</h1>
        </div>
        {isTeacher && (
          <div className="flex gap-2">
            <Button data-testid="new-lesson-button" variant="outline" onClick={() => setLessonOpen(true)}><Plus size={16} className="mr-1" /> Lesson</Button>
            <Button data-testid="new-assignment-button" onClick={() => setAsgOpen(true)} className="bg-slate-900 hover:bg-slate-800"><Plus size={16} className="mr-1" /> Assignment</Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="assignments">
        <TabsList>
          <TabsTrigger value="assignments" data-testid="lms-tab-assignments">Assignments</TabsTrigger>
          <TabsTrigger value="lessons" data-testid="lms-tab-lessons">Lessons</TabsTrigger>
        </TabsList>
        <TabsContent value="assignments" className="mt-4 space-y-3">
          {(assignments || []).map((a) => (
            <div key={a.id} className="bg-white border border-slate-200 rounded-lg p-4 stagger-in" data-testid={`assignment-card-${a.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display font-bold">{a.title}</p>
                <span className="text-xs text-slate-400">{a.subject} · Grade {a.grade}{a.section || ''} · due {a.due_at?.replace('T', ' ').slice(0, 16)} · {a.max_marks} marks</span>
                {a.my_submission && (
                  <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${a.my_submission.status === 'graded' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-700'}`} data-testid={`my-sub-status-${a.id}`}>
                    {a.my_submission.status === 'graded' ? `graded ${a.my_submission.marks}/${a.max_marks}` : a.my_submission.status}
                  </span>
                )}
              </div>
              {a.instructions && <p className="text-sm text-slate-600 mt-1">{a.instructions}</p>}
              {a.my_submission?.feedback && <p className="text-xs text-green-700 mt-1">Feedback: {a.my_submission.feedback}</p>}
              <div className="flex gap-2 mt-2">
                {user?.role === 'student' && a.my_submission?.status !== 'graded' && (
                  <button data-testid={`assignment-submit-${a.id}`} onClick={() => setSubmitFor(a)} className="text-xs bg-orange-600 text-white rounded px-2.5 py-1.5 hover:bg-orange-500">{a.my_submission ? 'Resubmit' : 'Submit work'}</button>
                )}
                {isTeacher && (
                  <>
                    <button data-testid={`assignment-subs-${a.id}`} onClick={() => setSubsFor(a)} className="text-xs border border-slate-200 rounded px-2.5 py-1.5 hover:border-slate-900">Submissions & grading</button>
                    <button data-testid={`assignment-delete-${a.id}`} onClick={() => { if (window.confirm('Delete assignment?')) delMut.mutate({ kind: 'assignments', id: a.id }); }} className="text-xs text-rose-600 underline">Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}
          {(assignments || []).length === 0 && <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400" data-testid="assignments-empty">No assignments yet.</div>}
        </TabsContent>
        <TabsContent value="lessons" className="mt-4 space-y-3">
          {(lessons || []).map((l) => (
            <div key={l.id} className="bg-white border border-slate-200 rounded-lg p-4 stagger-in" data-testid={`lesson-card-${l.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-display font-bold">{l.title}</p>
                <span className="text-xs text-slate-400">{l.subject} · Grade {l.grade}{l.section || ''} · by {l.created_by}</span>
                {isTeacher && <button data-testid={`lesson-delete-${l.id}`} onClick={() => { if (window.confirm('Delete lesson?')) delMut.mutate({ kind: 'lessons', id: l.id }); }} className="ml-auto text-xs text-rose-600 underline">Delete</button>}
              </div>
              {l.content && <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{l.content}</p>}
              <VideoEmbeds videos={l.videos} />
              <AttachmentLinks list={l.attachments} />
            </div>
          ))}
          {(lessons || []).length === 0 && <div className="bg-white border border-slate-200 rounded-lg p-10 text-center text-slate-400" data-testid="lessons-empty">No lessons posted yet.</div>}
        </TabsContent>
      </Tabs>

      <Dialog open={lessonOpen} onOpenChange={setLessonOpen}>
        <DialogContent data-testid="lesson-create-dialog">
          <DialogHeader><DialogTitle className="font-display">Post lesson</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <select data-testid="lesson-grade-select" value={lform.grade} onChange={(e) => setLform({ ...lform, grade: e.target.value })} className="w-32 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                {(meta?.grades || []).map((g) => <option key={g} value={g}>{/^\d+$/.test(g) ? `Grade ${g}` : g}</option>)}
              </select>
              <Input data-testid="lesson-subject-input" placeholder="Subject" value={lform.subject} onChange={(e) => setLform({ ...lform, subject: e.target.value })} />
            </div>
            <Input data-testid="lesson-title-input" placeholder="Lesson title" value={lform.title} onChange={(e) => setLform({ ...lform, title: e.target.value })} />
            <textarea data-testid="lesson-content-input" placeholder="Lesson content / notes..." value={lform.content} onChange={(e) => setLform({ ...lform, content: e.target.value })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-28" />
            <textarea data-testid="lesson-video-input" placeholder={'YouTube / Vimeo links - one per line\nhttps://youtu.be/...\nhttps://vimeo.com/...'} value={lform.video_text} onChange={(e) => setLform({ ...lform, video_text: e.target.value })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-16" />
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="lesson-attach-button" onClick={() => fileRef.current?.click()} className="text-xs border border-slate-200 rounded px-2 py-1.5 flex items-center gap-1"><Paperclip size={13} /> Attach PDF / Word / video</button>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) lessonUp.upload(e.target.files[0]); e.target.value = ''; }} />
              {lessonUp.pending.map((p) => <span key={p.file_id} className="text-xs bg-slate-100 rounded px-2 py-1">{p.name}</span>)}
            </div>
            <Button data-testid="lesson-post-submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!lform.title || !lform.subject || lessonMut.isPending} onClick={() => lessonMut.mutate()}>Post lesson</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={asgOpen} onOpenChange={setAsgOpen}>
        <DialogContent data-testid="assignment-create-dialog">
          <DialogHeader><DialogTitle className="font-display">Post assignment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <select data-testid="assignment-grade-select" value={aform.grade} onChange={(e) => setAform({ ...aform, grade: e.target.value })} className="w-32 border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
                {(meta?.grades || []).map((g) => <option key={g} value={g}>{/^\d+$/.test(g) ? `Grade ${g}` : g}</option>)}
              </select>
              <Input data-testid="assignment-subject-input" placeholder="Subject" value={aform.subject} onChange={(e) => setAform({ ...aform, subject: e.target.value })} />
            </div>
            <Input data-testid="assignment-title-input" placeholder="Assignment title" value={aform.title} onChange={(e) => setAform({ ...aform, title: e.target.value })} />
            <textarea data-testid="assignment-instructions-input" placeholder="Instructions..." value={aform.instructions} onChange={(e) => setAform({ ...aform, instructions: e.target.value })}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-20" />
            <div className="flex gap-2">
              <Input data-testid="assignment-due-input" type="datetime-local" value={aform.due_at} onChange={(e) => setAform({ ...aform, due_at: e.target.value })} />
              <Input data-testid="assignment-marks-input" type="number" className="w-28" placeholder="Max marks" value={aform.max_marks} onChange={(e) => setAform({ ...aform, max_marks: e.target.value })} />
            </div>
            <Button data-testid="assignment-post-submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!aform.title || !aform.subject || !aform.due_at || asgMut.isPending} onClick={() => asgMut.mutate()}>Post assignment</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!submitFor} onOpenChange={() => setSubmitFor(null)}>
        <DialogContent data-testid="submission-dialog">
          <DialogHeader><DialogTitle className="font-display">Submit - {submitFor?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <textarea data-testid="submission-text-input" placeholder="Your answer..." value={subText} onChange={(e) => setSubText(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-28" />
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid="submission-attach-button" onClick={() => subFileRef.current?.click()} className="text-xs border border-slate-200 rounded px-2 py-1.5 flex items-center gap-1"><Paperclip size={13} /> Attach file</button>
              <input ref={subFileRef} type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) submitUp.upload(e.target.files[0]); e.target.value = ''; }} />
              {submitUp.pending.map((p) => <span key={p.file_id} className="text-xs bg-slate-100 rounded px-2 py-1">{p.name}</span>)}
            </div>
            <Button data-testid="submission-submit" className="w-full bg-orange-600 hover:bg-orange-500" disabled={(!subText.trim() && submitUp.pending.length === 0) || submitMut.isPending} onClick={() => submitMut.mutate()}>Submit</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!subsFor} onOpenChange={() => setSubsFor(null)}>
        <DialogContent data-testid="submissions-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Submissions - {subsFor?.title}</DialogTitle></DialogHeader>
          <div className="divide-y divide-slate-100">
            {(subs || []).map((s) => (
              <div key={s.id} className="py-2" data-testid={`submission-row-${s.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm">{s.student_name}</span>
                  <span className={`font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded ${s.status === 'graded' ? 'bg-green-100 text-green-800' : s.status === 'late' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}`}>{s.status}{s.status === 'graded' ? ` ${s.marks}/${subsFor?.max_marks}` : ''}</span>
                </div>
                {s.text && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{s.text}</p>}
                <AttachmentLinks list={s.attachments} />
                {s.status !== 'graded' && (
                  <button data-testid={`grade-button-${s.id}`} onClick={() => {
                    const m = window.prompt(`Marks out of ${subsFor?.max_marks}:`);
                    if (m === null) return;
                    const fb = window.prompt('Feedback (optional):') || '';
                    gradeMut.mutate({ sid: s.id, marks: parseFloat(m), feedback: fb });
                  }} className="text-xs bg-slate-900 text-white rounded px-2 py-1 mt-1.5 hover:bg-slate-800">Grade</button>
                )}
                {s.feedback && <p className="text-xs text-green-700 mt-1">Feedback: {s.feedback}</p>}
              </div>
            ))}
            {(subs || []).length === 0 && <p className="py-4 text-sm text-slate-400">No submissions yet.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
